import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiX } from "react-icons/fi";
import "../styles/BottomSheet.css";

const ANIM_MS = 450;
const CLOSE_THRESHOLD = 120;

export default function BottomSheet({
  open,
  onClose,
  title,
  children,
  topOffset = 84,
}) {
  const { t } = useTranslation("cart");
  const sheetRef = useRef(null);
  const contentRef = useRef(null);

  const startY = useRef(0);
  const currentY = useRef(0);
  const canDrag = useRef(false);

  const [dragging, setDragging] = useState(false);
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return;
    }

    if (mounted) {
      setClosing(true);
      const tmr = setTimeout(() => {
        setMounted(false);
        setClosing(false);
      }, ANIM_MS);
      return () => clearTimeout(tmr);
    }
  }, [open, mounted]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }

    if (mounted) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mounted, onClose]);

  function closeSmooth() {
    onClose?.();
  }

  function onTouchStart(e) {
    if (!sheetRef.current) return;

    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;

    const scroller = contentRef.current;
    const isAtTop = !scroller || scroller.scrollTop <= 0;

    canDrag.current = isAtTop;
    setDragging(false);
    sheetRef.current.style.transition = "none";
  }

  function onTouchMove(e) {
    if (!sheetRef.current) return;

    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    if (diff <= 0) {
      return;
    }

    if (!canDrag.current) {
      return;
    }

    setDragging(true);
    sheetRef.current.style.transform = `translateY(${diff}px)`;
  }

  function onTouchEnd() {
    if (!sheetRef.current) return;

    const diff = currentY.current - startY.current;
    sheetRef.current.style.transition = "";

    if (dragging && diff > CLOSE_THRESHOLD) {
      sheetRef.current.style.transform = "";
      closeSmooth();
    } else {
      sheetRef.current.style.transform = "";
    }

    canDrag.current = false;
    setDragging(false);
  }

  if (!mounted) return null;

  const overlayClass = `bsOverlay ${open && !closing ? "isOpen" : "isClosing"}`;

  return (
    <div
      className={overlayClass}
      style={{ paddingTop: topOffset }}
      onClick={closeSmooth}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`bsSheet ${dragging ? "isDragging" : ""}`}
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="bsHandleWrap">
          <div className="bsHandle" />
        </div>

        <div className="bsTopBar">
          <div className="bsTitle fs-20 fw-bold">{title || t("my_cart")}</div>

          <button
            type="button"
            className="bsCloseBtn"
            onClick={closeSmooth}
            aria-label={t("close")}
            title={t("close")}
          >
            <FiX className="bsCloseIcon" />
          </button>
        </div>

        <div className="bsContent" ref={contentRef}>
          {children}
        </div>
      </div>
    </div>
  );
}